import { type ReactElement } from 'react';
import { querySuiteRunStatusSummary } from '@/features/executions/data';
import {
  buildSuiteCounts,
  type SuiteStatusKey,
} from '@/features/quality/components/qualityStatusDataHelpers';
import { EntityTrendsPageShell } from '@/features/shared/components/entityTrends/EntityTrendsPageShell';
import { TestingListEmptyState } from '@/features/shared/components/TestingListEmptyState';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { querySuites, querySuiteSummary } from '@/features/suites/data/suites';
import { SuitesPageContent } from '@/features/suites/pages/SuitesPageContent';
import { suiteRunStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import { SuitesIcon } from '@/ui/primitives/icons';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteRunStatusSummaryDto,
  SuiteListItemDto,
  SuiteSummaryDto,
} from '@flowqa/shared';

const noop = (): void => {
  void 0;
};

const SuitesPageInner = (): ReactElement => {
  const viewModel = useEntityTrendsPage<
    SuiteListItemDto,
    SuiteSummaryDto | SuiteRunStatusSummaryDto
  >({
    loadingMessageKey: 'suites',
    metricsScope: 'suite',
    loadExecutionMetrics: false,
    gridPath: gridPaths.suites,
    executionGridPath: gridPaths.suiteRuns,
    entitySearchField: 'name',
    executionSearchField: 'suiteName',
    statusOptions: suiteRunStatusOptions.map((option) => ({
      value: option.value as SuiteStatusKey,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteRunSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.suiteSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.suiteRunSummaryPrefix,
    queryLatestSummary: querySuiteSummary,
    queryExecutionSummary: querySuiteRunStatusSummary,
    buildCounts: buildSuiteCounts,
    selectThresholds: (thresholds) => thresholds.suites,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled: _deltasEnabled,
      },
    ): Promise<QueryResult<SuiteListItemDto>> =>
      querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          latestRun: true,
          executionStatusCounts: true,
          latestTestResults: { counts: true, summaries: true },
          metrics: true,
        },
      }),
  });
  const suitesEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'suite' });
  const suiteRunsEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'suiteRun' });
  const openUploadDialog =
    (viewModel as { uploadFlow?: { openUploadDialog?: () => void } }).uploadFlow
      ?.openUploadDialog ?? noop;
  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Suites"
        addButtonLabel="Add Suite"
        searchAriaLabel="Search suites"
        chartTabsAriaLabel="Suites chart tabs"
        pageTabsAriaLabel="Suites page tabs"
        entityTabLabel="Suites"
        chartEmptyStateTitle={suiteRunsEmptyState}
        entityTableNoRowsText={suitesEmptyState}
        executionTableNoRowsText={suiteRunsEmptyState}
        contentOverride={
          viewModel.isKnownEmpty ? (
            <TestingListEmptyState
              body="Get started by uploading your first results file to see suite insights, trends and quality metrics."
              icon={SuitesIcon}
              title={getEmptyStateMessage('entity.noneYetForProject', { entity: 'suite' })}
              onUploadClick={openUploadDialog}
            />
          ) : (
            <SuitesPageContent viewModel={viewModel} />
          )
        }
        hideHeader={viewModel.isKnownEmpty}
        hideTitleActions={viewModel.isKnownEmpty}
      />
    </Stack>
  );
};

export const SuitesPage = (): ReactElement => {
  return <SuitesPageInner />;
};
